<template>
    <div class="input-group mb-3" :class="{ 'has-error': hasError }" :style="{maxWidth: maxWidth}">
        <label v-if="label" :for="id">{{ label }}</label>
        <div style="display: flex"
             :class="{clickable: copyField}"
             @click="copyToClipboard">
            <input v-if="!textField"
                   :id="id"
                   :ref="id"
                   :type="type === 'number' ? 'text' : type"
                   :value="_value"
                   :class="{append: copyField}"
                   :maxlength="maxLength || 999"
                   :placeholder="placeholder"
                   @blur="onBlur"
                   @focus="onFocus"
                   @keydown="onKeyDown"
                   @input="dataChanged"
                   :autofocus="autofocus ? true : false"
                   :readonly="readOnly || copyField"/>
            <textarea v-else
                      :id="id"
                      :ref="id"
                      :value="_value"
                      :class="{append: copyField}"
                      :maxlength="maxLength || 999"
                      :placeholder="placeholder"
                      rows="5"
                      @blur="onBlur"
                      @focus="onFocus"
                      @keydown="onKeyDown"
                      @input="dataChanged"
                      :autofocus="autofocus ? true : false"
                      :readonly="readOnly || copyField"/>
            <div v-if="copyField" class="input-group-append"></div>
        </div>
        <span class="error-message" :class="{ show: hasError }">
            <slot name="error"></slot>
        </span>
    </div>
</template>

<script lang="ts">
import {Component, Prop, Vue} from "vue-property-decorator";

@Component
export default class InputField extends Vue {
    @Prop({type: Boolean, default: false}) autofocus!: boolean;
    @Prop({type: String, default: ""}) placeholder!: string;
    @Prop({type: String}) label!: string;
    @Prop({required: true}) value!: string | number;
    @Prop({type: Boolean, default: false}) required!: boolean;
    @Prop({type: Function, default: undefined}) validator!: Function;
    @Prop({type: RegExp, default: undefined}) validatorRegExp!: RegExp;
    @Prop({type: String, default: "text"}) type!: string;
    @Prop({type: Number, default: 0}) maxLength!: number;
    @Prop({type: String, default: "none"}) maxWidth!: string;
    @Prop({type: Boolean, default: false}) copyField!: boolean;
    @Prop({type: Boolean, default: false}) readOnly!: boolean;
    @Prop({type: Boolean, default: false}) textField!: boolean;

    private id: string = Math.random().toString(36).substr(2, 5);
    private suppressError: boolean = true;
    private hadFirstFocus: boolean = false;
    private showSuccess: boolean = false;

    get _value(): string | number {
        return this.value;
    }

    get inputValid(): boolean {
        return Boolean(this.value === "" && !this.required) || this._validator(this.value + "");
    }

    get hasError(): boolean {
        return !this.inputValid && !this.suppressError && this.hadFirstFocus;
    }

    mounted() {
        if (this.autofocus) {
            this.$nextTick(() => {
                (this.$refs[this.id] as HTMLInputElement)?.focus();
            });
        }
    }

    private dataChanged(e: Event) {
        const value: string = (e.target as HTMLTextAreaElement)?.value;
        this.$emit("update:value", this.type === "number" ? Number(value) : value);
    }

    //  If this returns false, an error may be shown.
    private _validator(value: string): boolean {
        if (typeof this.validator === "function") {
            return this.validator(value);
        }
        if (this.validatorRegExp === undefined) {
            return Boolean(this.value || !this.required);
        }
        return this.validatorRegExp.test(value);
    }

    private onFocus() {
        this.suppressError = true;
        this.$emit("focus");
    }

    private onBlur() {
        this.suppressError = false;
        this.$emit("blur");
    }

    //  Method to call when form gets submitted. Returns false
    //  if input not valid, and shows error message...
    public submit(): boolean {
        this.suppressError = false;
        this.hadFirstFocus = true;
        return this.inputValid;
    }

    public reset() {
        this.suppressError = true;
        this.hadFirstFocus = false;
    }

    //  On form submit (enter button press) show potential errors.
    //  So set suppressError to false.
    private onKeyDown(e: KeyboardEvent) {
        this.$set(this, "suppressError", !(e.key === "Enter"));
        if (e.key === "Enter") {
            this.$emit("enter-pressed");
        } else {
            this.$set(this, "hadFirstFocus", true);
        }
    }

    private copyToClipboard() {
        if (!this.copyField) return;
        (this.$refs[this.id] as HTMLInputElement).select();
        document.execCommand("copy");
        this.showSuccess = true;
        this.$toastr.success(this.$t("components.inputfield.copied"), true, true);
    }
}
</script>

<style lang="scss" scoped>
.input-group {
    position: relative;
    overflow: hidden;

    label {
        @include fontCaption();
        margin-bottom: 3px;
    }

    .clickable:hover {
        cursor: pointer;
    }

    input,
    textarea {
        height: 40px;
        background-color: $greyShade1;
        border-color: $greyShade3;
        border-style: solid;
        border-width: 1px;
        border-radius: 3px;
        width: 100%;
        padding: 10px 8px 12px;
        font-family: "FFMark W04", "Helvetica Neue", Arial, sans-serif;
        font-size: 16px;
        font-weight: 500;
        flex: 1;

        &.append {
            border-radius: 3px 0 0 3px;
        }

        &:focus {
            box-shadow: 0 0 6px $greyShade3;
            outline: none;
        }

        &[readonly] {
            color: $greyShade5;
        }

        &::selection {
            background-color: $greyShade3;
        }
    }

    textarea {
        resize: none;
        height: inherit;
    }

    .input-group-append {
        background: white url("../../assets/icons/copy-red.svg") no-repeat center;
        border: solid 1px $greyShade3;
        border-radius: 0 3px 3px 0;
        border-left: none;
        width: 40px;
        height: 40px;
        background-size: 16px 16px;
        transition: border-color 0.15s ease-in-out;

        &:hover {
            cursor: pointer;
        }
    }

    .error-message {
        color: $tendencyRed;
        font-size: 14px;
        display: block;
        overflow-y: hidden;
        max-height: 0;
        transition-property: all;
        transition-duration: .5s;
        transition-timing-function: cubic-bezier(.29, .64, .33, 1);

        &.show {
            max-height: 500px;
            margin: 4px 0 0 0;
        }
    }

    &.has-error, {
        input {
            border-color: $tendencyRed;
        }

        span {
            margin-top: 4px !important;
        }
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }
}

@media only screen and (max-width: $breakPoint - 1px) {
    .input-group {
        label {
            font-size: 12px;
        }

        input {
            height: 44px;
        }

        .input-group-append {
            width: 44px;
            height: 44px;
            background-size: 20px 20px;
        }
    }
}
</style>
